import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ThemadagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ButtonState = 'idle' | 'loading' | 'success' | 'error';

const ThemadagDialog: React.FC<ThemadagDialogProps> = ({ open, onOpenChange }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonState, setButtonState] = useState<ButtonState>('idle');
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setButtonState('loading');

    const formData = new FormData(e.currentTarget as HTMLFormElement);
    const data = {
      voornaam: formData.get('voornaam') as string,
      achternaam: formData.get('achternaam') as string,
      email: formData.get('email') as string,
      telefoon: formData.get('telefoon') as string || undefined,
      organisatie: formData.get('organisatie') as string || undefined,
      bericht: formData.get('bericht') as string || undefined,
    };

    try {
      // Save form data to sessionStorage to restore if payment is canceled
      sessionStorage.setItem('themadag_form_data', JSON.stringify(data));

      // Create Stripe checkout session
      const response = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'themadag',
          eventId: 'themadag-burnout-2026-10-22',
          eventName: 'Themadag: Burn-out in organisaties',
          eventDatum: '22 oktober 2026',
          eventTijd: '13:00 - 17:00',
          eventLocatie: 'Stadsoase naar Zee, Amsterdam (IJburg)',
          userEmail: data.email,
          userData: data,
        }),
      });

      const result = await response.json();

      if (!response.ok || result.error) {
        throw new Error(result.error || 'Fout bij aanmaken van betalingslink');
      }

      if (result.url) {
        // Redirect to Stripe checkout
        window.location.href = result.url;
      } else {
        throw new Error('Geen checkout URL ontvangen');
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      setButtonState('error');
      toast({
        title: "Er ging iets mis",
        description: error instanceof Error ? error.message : "Probeer het opnieuw of neem direct contact op via telefoon.",
        variant: "destructive",
      });

      setTimeout(() => {
        setButtonState('idle');
        setIsSubmitting(false);
      }, 3000);
    }
  };

  // Load saved form data if available
  React.useEffect(() => {
    if (open) {
      const savedData = sessionStorage.getItem('themadag_form_data');
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          // Pre-fill form fields (you could enhance this to actually set form values)
          console.log('Restored form data:', parsed);
        } catch (e) {
          console.error('Failed to parse saved form data:', e);
        }
      }
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-noto-serif">Aanmelden voor Themadag Burn-out</DialogTitle>
          <DialogDescription>
            Vul het formulier in om je aan te melden. Je wordt doorgestuurd naar een veilige betaalomgeving.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="voornaam">Voornaam *</Label>
              <Input
                id="voornaam"
                name="voornaam"
                required
                disabled={isSubmitting}
                placeholder="Je voornaam"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="achternaam">Achternaam *</Label>
              <Input
                id="achternaam"
                name="achternaam"
                required
                disabled={isSubmitting}
                placeholder="Je achternaam"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              disabled={isSubmitting}
              placeholder="je@email.nl"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="telefoon">Telefoon</Label>
            <Input
              id="telefoon"
              name="telefoon"
              type="tel"
              disabled={isSubmitting}
              placeholder="0612345678"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="organisatie">Organisatie</Label>
            <Input
              id="organisatie"
              name="organisatie"
              disabled={isSubmitting}
              placeholder="Je organisatie (optioneel)"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bericht">Vragen of opmerkingen</Label>
            <Textarea
              id="bericht"
              name="bericht"
              rows={4}
              disabled={isSubmitting}
              placeholder="Eventuele vragen of opmerkingen (optioneel)"
            />
          </div>

          {/* Pricing Info */}
          <div className="bg-[#F9EBEF] p-4 rounded-lg border border-[#854C73]/20">
            <p className="text-sm text-[#854C73] font-semibold mb-2">Investering</p>
            <p className="text-xs text-[#854C73]/80">
              <span className="font-bold">€ 100</span> excl. BTW (early bird t/m 1 juni 2026)
              <br />
              <span className="font-bold">€ 150</span> excl. BTW (regulier tarief)
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || buttonState === 'success'}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {buttonState === 'loading' && (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Bezig met doorsturen...
                </>
              )}
              {buttonState === 'success' && (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Doorsturen...
                </>
              )}
              {buttonState === 'error' && 'Opnieuw proberen'}
              {buttonState === 'idle' && 'Naar betaling'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ThemadagDialog;

